import * as React from "react";
import { ContactsOutlined } from "@ant-design/icons";
import { Card } from "antd5";

import { ButtonLink, TextLink } from "components/actions/Links";
import { generateContactsUrl } from "components/contacts/utils";

import css from "./ContactCard.module.scss";

type Props = {
  name?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  buyerIds?: string[];
  showWebsite?: boolean;
  showContactsButton?: boolean;
  isLoading?: boolean;
  className?: string;
  /** Laid out in the page's flow rather than in a card, under a page-level heading. */
  inline?: boolean;
};

type ContactLinkProps = {
  contactLink: string | undefined;
  contactLinkType?: "website" | "email";
};

function ContactLink({ contactLink, contactLinkType = "website" }: ContactLinkProps) {
  if (!contactLink) {
    return <span>--</span>;
  }

  return (
    <TextLink
      targetType="new-tab"
      to={contactLinkType === "website" ? contactLink : `mailto:${contactLink}`}
      className={css.link}
    >
      {contactLink}
    </TextLink>
  );
}

/**
 * Given a {@link ContactDetailsDto} object, renders a card with the contact details that are present.
 * @param param0
 * @returns
 */
export default function ContactCard({
  name,
  address,
  email,
  phone,
  website,
  buyerIds,
  showWebsite,
  showContactsButton,
  isLoading,
  className,
  inline = false,
}: Props): JSX.Element {
  // Only the buyer scope; the contacts page merges the rest with its own defaults, so
  // spreading them here would pad the URL without changing the search.
  const contactsUrl = generateContactsUrl({ buyerGuids: buyerIds });

  const Heading = inline ? "h2" : "h3";
  const details = (
    <div className={css.cardContent}>
      <Heading className={css.title}>Procurement contact</Heading>
      <div className={css.contactDeets}>
        <ContactDetail className={className} title="Name" value={name} />
        <ContactDetail
          title="Email"
          value={<ContactLink contactLink={email} contactLinkType="email" />}
        />
        <ContactDetail title="Telephone" value={phone} />
        <ContactDetail title="Address" value={address} />
        {showWebsite && (
          <ContactDetail title="Website" value={<ContactLink contactLink={website} />} />
        )}
      </div>
      {showContactsButton && (
        <ButtonLink to={contactsUrl} type="default" targetType="new-tab">
          <ContactsOutlined className={css.link} /> Find decision maker
        </ButtonLink>
      )}
    </div>
  );

  return inline ? (
    <section aria-label="Contact details">{details}</section>
  ) : (
    <Card loading={isLoading} aria-label="Contact details">
      {details}
    </Card>
  );
}

type ContactDetailProps = {
  title: string;
  value?: React.ReactNode;
  className?: string;
};
/**
 * Displays the title and value of a ContactDetail, if it exists.
 * @param param0
 * @returns
 */
function ContactDetail({ title, value, className }: ContactDetailProps): JSX.Element {
  return (
    <>
      <span className={className ?? css.detail}>{title}</span>
      <span className={css.value}>{value || "--"}</span>
    </>
  );
}
